import { guardGameSurface } from './186-game-surface.mjs';
import { paintIconAction } from './82-icon-actions.mjs';
import {
  createOrchardRush,
  resizeOrchardRush,
  stepOrchardRush,
  sliceOrchardRush,
} from '../../../packages/domain/src/65-orchard-rush.mjs';
import { createOrchardCanvas } from './72-orchard-canvas.mjs';

const names = ['apple', 'mandarin', 'lemon', 'plum', 'watermelon', 'kiwi'];
/** An explicitly started private arcade round. RAF owns presentation only and
 * spends time solely while visible, permitted and unpaused. Native fruit buttons
 * offer keyboard/tap access alongside captured, segment-tested cutting gestures. */
export function createOrchardRushPlayer(
  ctx,
  {
    host,
    seed = 7,
    allowed = () => true,
    visible = () => true,
    onProgress = () => {},
    onStart = () => {},
    rendererFactory = createOrchardCanvas,
  } = {},
) {
  const root = ctx.e('section', 'rush-game');
  const hud = ctx.e('div', 'rush-hud');
  const score = ctx.e('strong', '', '0'),
    time = ctx.e('span', '', '60s'),
    combo = ctx.e('span', '', 'LOCAL ROUND');
  time.className = 'rush-clock';
  const comboMeter = ctx.e('progress', 'rush-combo-meter');
  comboMeter.max = 3;
  comboMeter.value = 0;
  comboMeter.setAttribute('aria-label', 'Fruit in this stroke; three earns a combo');
  hud.append(score, combo, time, comboMeter);
  const stage = ctx.e('div', 'rush-stage');
  const canvas = ctx.e('canvas', 'rush-canvas');
  canvas.setAttribute('aria-hidden', 'true');
  const targets = ctx.e('div', 'rush-targets');
  targets.setAttribute('aria-label', 'Flying fruit');
  const overlay = ctx.e('div', 'rush-overlay');
  const title = ctx.e('h3', '', 'Make a juicy entrance');
  const description = ctx.e(
    'p',
    '',
    'Sweep through fruit. Catch three in one stroke for a combo. Watch the thorn pods.',
  );
  const play = ctx.e('button', 'game-action rush-start', 'Play 60 seconds');
  play.type = 'button';
  const tips = ctx.e('ol', 'rush-intro-steps');
  for (const text of ['① Let fruit rise', '② Sweep through 3+', '③ Avoid thorn pods'])
    tips.append(ctx.e('li', '', text));
  overlay.append(title, description, tips, play);
  stage.append(canvas, targets, overlay);
  const controls = ctx.e('div', 'rush-controls');
  const pause = ctx.e('button', 'game-action', 'Pause');
  pause.type = 'button';
  paintIconAction(ctx, pause, 'pause', 'Pause this round');
  pause.disabled = true;
  const slice = ctx.e('button', 'game-action rush-slice', 'Slice nearest fruit');
  slice.type = 'button';
  slice.disabled = true;
  slice.setAttribute('aria-label', 'Slice the nearest visible fruit');
  const instruction = ctx.e('span', '', 'Drag or tap fruit · keyboard: use Slice nearest fruit');
  controls.append(instruction, slice, pause);
  const live = ctx.e('p', 'rush-announcement');
  live.setAttribute('role', 'status');
  root.append(hud, stage, controls, live);
  host.append(root);
  const renderer = rendererFactory(canvas);
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  let state = createOrchardRush(seed),
    running = false,
    paused = false,
    disposed = false;
  let frame = 0,
    previous = 0,
    stroke = 0,
    pointer = null,
    suppress = false,
    nextLevel = 1;
  const nodes = new Map();
  const canRun = () => !disposed && allowed() && visible() && !document.hidden;
  function report() {
    onProgress({ score: state.score, stage: state.level, completed: state.completed });
  }
  function paint() {
    const active = running && !paused && canRun() && !state.completed;
    stage.dataset.running = String(active);
    slice.disabled = !active;
    pause.disabled = !running || !canRun() || state.completed;
    score.textContent = state.score.toLocaleString();
    time.textContent = `${Math.ceil((60000 - state.elapsed) / 1000)}s`;
    const currentStroke = pointer?.stroke === state.stroke ? state.strokeCount : 0;
    combo.textContent =
      currentStroke > 0
        ? `${currentStroke} IN THIS STROKE${currentStroke >= 3 ? ' ✦ COMBO' : ' · AIM FOR 3'}`
        : `${state.sliced} SLICED · BEST ${state.bestCombo}`;
    comboMeter.value = Math.min(3, currentStroke);
    root.dataset.finale = String(!state.completed && state.elapsed >= 50000);
    root.dataset.paused = String(paused);
    time.setAttribute(
      'aria-label',
      `${Math.ceil((60000 - state.elapsed) / 1000)} seconds remaining`,
    );
    const activeIds = new Set(state.fruits.map((f) => f.id));
    for (const [id, node] of nodes)
      if (!activeIds.has(id)) {
        if (document.activeElement === node) slice.focus({ preventScroll: true });
        node.remove();
        nodes.delete(id);
      }
    for (const fruit of state.fruits) {
      let node = nodes.get(fruit.id);
      if (!node) {
        node = ctx.e('button', 'rush-fruit-target');
        node.type = 'button';
        // A stable Slice control owns sequential keyboard navigation; moving
        // targets remain named direct-touch controls outside the tab order.
        node.tabIndex = -1;
        node.setAttribute(
          'aria-label',
          fruit.thorn ? 'Thorn pod · avoid' : `Slice ${names[fruit.kind]}`,
        );
        node.addEventListener('click', (event) => {
          if (suppress && event.detail !== 0) {
            suppress = false;
            event.preventDefault();
            return;
          }
          const current = state.fruits.find((f) => f.id === fruit.id);
          if (!current || !running || paused || !canRun()) return;
          cut(current, current, ++stroke);
        });
        targets.append(node);
        nodes.set(fruit.id, node);
      }
      node.disabled = !running || paused || !canRun() || state.completed;
      node.style.left = `${(fruit.x / state.world.width) * 100}%`;
      node.style.top = `${(fruit.y / state.world.height) * 100}%`;
      node.style.width = `${((fruit.radius * 2) / state.world.width) * 100}%`;
      node.style.height = `${((fruit.radius * 2) / state.world.height) * 100}%`;
    }
    if (state.completed) {
      running = false;
      pause.disabled = true;
      slice.disabled = true;
      overlay.hidden = false;
      title.textContent = 'Harvest complete!';
      description.textContent = `${state.score.toLocaleString()} private points · ${state.sliced} fruit · best combo ${state.bestCombo}. ${state.bestCombo >= 6 ? 'Six-fruit mastery!' : state.bestCombo >= 3 ? 'Combo harvest!' : 'Next time, let a cluster rise and slice three in one sweep.'}`;
      tips.hidden = true;
      play.textContent = 'Next harvest';
      if (document.activeElement === slice || document.activeElement === pause)
        play.focus({ preventScroll: true });
    }
  }
  function cut(from, to, id) {
    if (!running || paused || !canRun()) return;
    const candidate = sliceOrchardRush(state, from, to, id);
    renderer.swipe(to);
    if (candidate === state) return;
    state = candidate;
    renderer.effects(state.events, reduced.matches);
    const award = state.events.find((e) => e.type === 'combo' || e.type === 'thorn');
    if (award)
      live.textContent =
        award.type === 'combo'
          ? `${award.count} fruit combo. Plus ${award.points} points.`
          : 'Thorn pod. Streak broken.';
    paint();
    report();
  }
  function sliceNearest() {
    if (!running || paused || !canRun() || state.completed) return;
    const fruit = state.fruits
      .filter(
        (f) =>
          !f.thorn && f.x >= 0 && f.x <= state.world.width && f.y >= 0 && f.y <= state.world.height,
      )
      .sort(
        (a, b) =>
          Math.hypot(a.x - state.world.width / 2, a.y - state.world.height / 2) -
            Math.hypot(b.x - state.world.width / 2, b.y - state.world.height / 2) || a.id - b.id,
      )[0];
    if (!fruit) {
      live.textContent = 'Wait for fruit to enter the orchard.';
      return;
    }
    cut(fruit, fruit, ++stroke);
    live.textContent = `${names[fruit.kind]} sliced. ${state.score} local points.`;
  }
  function point(event) {
    const rect = stage.getBoundingClientRect();
    return {
      x: Math.max(
        -100,
        Math.min(
          state.world.width + 100,
          ((event.clientX - rect.left) / rect.width) * state.world.width,
        ),
      ),
      y: Math.max(
        -100,
        Math.min(
          state.world.height + 100,
          ((event.clientY - rect.top) / rect.height) * state.world.height,
        ),
      ),
    };
  }
  function down(event) {
    if (
      !running ||
      paused ||
      !canRun() ||
      pointer ||
      event.button !== 0 ||
      event.isPrimary === false
    )
      return;
    const start = point(event);
    pointer = { id: event.pointerId, last: start, stroke: ++stroke, moved: false };
    suppress = false;
    event.preventDefault?.();
    stage.setPointerCapture?.(event.pointerId);
    cut(start, start, stroke);
  }
  function move(event) {
    if (!pointer || pointer.id !== event.pointerId) return;
    const next = point(event);
    if (Math.hypot(next.x - pointer.last.x, next.y - pointer.last.y) > 1) pointer.moved = true;
    event.preventDefault();
    const samples = event.getCoalescedEvents?.() || [];
    for (const sample of samples) {
      const at = point(sample);
      cut(pointer.last, at, pointer.stroke);
      pointer.last = at;
    }
    cut(pointer.last, next, pointer.stroke);
    pointer.last = next;
  }
  function release(event) {
    if (!pointer || pointer.id !== event.pointerId) return;
    if (event.type === 'pointerup') {
      const next = point(event);
      cut(pointer.last, next, pointer.stroke);
      suppress = true;
    }
    const id = pointer.id;
    pointer = null;
    if (stage.hasPointerCapture?.(id)) stage.releasePointerCapture(id);
    paint();
  }
  function tick(now) {
    frame = 0;
    if (!canRun()) {
      previous = 0;
      return;
    }
    if (running && !paused) {
      const dt = previous ? Math.min(50, now - previous) : 0;
      if (dt) state = stepOrchardRush(state, dt);
      paint();
      if (state.completed) {
        report();
        live.textContent = `Harvest complete. ${state.score} points.`;
      }
    }
    previous = now;
    renderer.draw(state, state.elapsed, reduced.matches);
    if (running && !paused) frame = requestAnimationFrame(tick);
  }
  function clearPointer() {
    const id = pointer?.id;
    pointer = null;
    if (id !== undefined && stage.hasPointerCapture?.(id)) stage.releasePointerCapture(id);
  }
  function resize() {
    if (disposed) return;
    const rect = stage.getBoundingClientRect();
    if (!(rect.width > 0 && rect.height > 0)) return;
    const height = rect.width > rect.height ? 320 : 540;
    const width = (height * rect.width) / rect.height;
    if (width < 160 || width > 4096) return;
    const next = resizeOrchardRush(state, { width, height });
    if (next === state) return;
    clearPointer();
    state = next;
    renderer.clear();
    paint();
    if (canRun()) renderer.draw(state, state.elapsed, reduced.matches);
  }
  function refresh() {
    if (!canRun()) {
      if (running && !paused) pauseRound();
      stage.dataset.running = 'false';
      slice.disabled = true;
      pause.disabled = true;
      clearPointer();
      cancelAnimationFrame(frame);
      frame = 0;
      previous = 0;
      return;
    }
    paint();
    if (!frame) frame = requestAnimationFrame(tick);
  }
  function pauseRound() {
    if (!running) return;
    paused = true;
    clearPointer();
    previous = 0;
    cancelAnimationFrame(frame);
    frame = 0;
    paintIconAction(ctx, pause, 'play', 'Resume this round');
    overlay.hidden = false;
    title.textContent = 'Harvest on hold';
    description.textContent = `${state.score.toLocaleString()} points banked · ${Math.ceil((60000 - state.elapsed) / 1000)} seconds remain. Your fruit and timer are paused.`;
    tips.hidden = true;
    play.textContent = 'Resume harvest';
    paint();
  }
  function begin() {
    if (!allowed() || disposed) return;
    onStart();
    state = createOrchardRush(seed, nextLevel, state.world);
    nextLevel = Math.min(20, nextLevel + 1);
    running = true;
    paused = false;
    previous = 0;
    overlay.hidden = true;
    pause.disabled = false;
    paintIconAction(ctx, pause, 'pause', 'Pause this round');
    live.textContent = 'Harvest started. Slice fruit; avoid thorn pods.';
    renderer.clear();
    report();
    refresh();
  }
  function togglePause() {
    if (paused) {
      paused = false;
      overlay.hidden = true;
      previous = 0;
      paintIconAction(ctx, pause, 'pause', 'Pause this round');
      refresh();
    } else pauseRound();
  }
  const listeners = [
    ['pointerdown', down],
    ['pointermove', move],
    ['pointerup', release],
    ['pointercancel', release],
    ['lostpointercapture', release],
    [
      'contextmenu',
      (event) => {
        if (running && !paused) event.preventDefault();
      },
    ],
    ['dragstart', (event) => event.preventDefault()],
  ];
  for (const [event, listener] of listeners) stage.addEventListener(event, listener);
  const unguard = guardGameSurface(stage, {
    ownsTouch: () => running && !paused && canRun(),
    cancel: clearPointer,
  });
  const playAction = () => (paused ? togglePause() : begin());
  play.addEventListener('click', playAction);
  pause.addEventListener('click', togglePause);
  slice.addEventListener('click', sliceNearest);
  document.addEventListener('visibilitychange', refresh);
  reduced.addEventListener?.('change', refresh);
  const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null;
  observer?.observe(stage);
  resize();
  renderer.draw(state, state.elapsed, reduced.matches);
  refresh();
  return {
    refresh,
    pause: pauseRound,
    cancel() {
      clearPointer();
      renderer.clear();
      previous = 0;
      refresh();
    },
    dispose() {
      disposed = true;
      unguard();
      observer?.disconnect();
      clearPointer();
      cancelAnimationFrame(frame);
      nodes.clear();
      for (const [event, listener] of listeners) stage.removeEventListener(event, listener);
      play.removeEventListener('click', playAction);
      pause.removeEventListener('click', togglePause);
      slice.removeEventListener('click', sliceNearest);
      document.removeEventListener('visibilitychange', refresh);
      reduced.removeEventListener?.('change', refresh);
      root.remove();
    },
  };
}
